import React, { Suspense, useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "../data/site";
import {
  useLanguage,
  getEnglishPath,
} from "../i18n/LanguageContext";
import { translate } from "../i18n/translations";

const MobileMenu = React.lazy(() => import("./MobileMenu"));

export function Logo({ dark, to = "/" }) {
  return (
    <Link to={to} data-testid="header-logo" className="flex items-center gap-2.5 shrink-0" aria-label="CAB CITY — Accueil">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ccblue font-heading text-lg font-extrabold text-white shadow-[0_6px_16px_-4px_rgba(0,82,255,0.5)]">
        C
      </span>
      <span className={`font-heading text-lg font-extrabold tracking-tight ${dark ? "text-white" : "text-navy"}`}>
        CAB&nbsp;CITY
      </span>
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { language, switchLanguage } = useLanguage();
  const t = (key) => translate(language, key);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <a
        href="#contenu-principal"
        data-testid="skip-to-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-xl focus:bg-ccblue focus:px-5 focus:py-3 focus:font-heading focus:text-sm focus:font-bold focus:text-white"
      >
        Aller au contenu
      </a>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b border-line/70 bg-white/95 backdrop-blur-xl transition-shadow duration-300 ${
          scrolled ? "shadow-[0_4px_24px_-12px_rgba(11,19,43,0.15)]" : ""
        }`}
      >
        <div className="container-site flex h-[72px] items-center justify-between gap-4">
          <Logo to={language === "en" ? "/en/" : "/"} />
          <nav aria-label="Navigation principale" className="hidden items-center gap-1 lg:flex">
              {NAV_LINKS.map((l) => (
               <NavLink
                key={l.to}
                to={language === "en" ? getEnglishPath(l.to) : l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `rounded-lg px-3.5 py-2 font-heading text-[13.5px] font-semibold transition-colors duration-200 ${
                    isActive ? "text-ccblue" : "text-navy/75 hover:text-navy"
                  }`
                }
              >
                {translate(language, `nav.${l.key}`)}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2.5">
            <div
  className="hidden items-center rounded-lg border border-line bg-white px-2 py-1.5 font-heading text-xs font-bold md:flex"
  aria-label="Language selector"
>
  <button
    type="button"
    onClick={() => switchLanguage("fr")}
    aria-current={language === "fr" ? "true" : undefined}
    aria-label={t("language.switchToFrench")}
    className={`px-1.5 transition-colors ${
      language === "fr"
        ? "text-ccblue"
        : "text-navy/45 hover:text-navy"
    }`}
  >
    FR
  </button>

  <span className="text-navy/25" aria-hidden="true">
    |
  </span>

  <button
    type="button"
    onClick={() => switchLanguage("en")}
    aria-current={language === "en" ? "true" : undefined}
    aria-label={t("language.switchToEnglish")}
    className={`px-1.5 transition-colors ${
      language === "en"
        ? "text-ccblue"
        : "text-navy/45 hover:text-navy"
    }`}
  >
    EN
  </button>
</div>
            <a
              href={PHONE_TEL}
              data-testid="header-phone-link"
              className="hidden items-center gap-2 rounded-xl border border-line bg-white px-4 py-2.5 font-heading text-[13.5px] font-bold text-navy transition-colors hover:border-ccblue hover:text-ccblue md:inline-flex"
            >
              <Phone size={15} strokeWidth={2.4} />
              {PHONE_DISPLAY}
            </a>
            <a href="#reserver" data-testid="header-cta-booking" className="booking-cta btn-primary !px-5 !py-2.5 !text-[13.5px]">
              {t("actions.book")}
            </a>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-white text-navy lg:hidden"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <Suspense fallback={null}>
          <MobileMenu
            open={open}
            setOpen={setOpen}
            language={language}
            switchLanguage={switchLanguage}
          />
        </Suspense>
      )}
    </>
  );
}
